import { RefObject, useEffect, useRef, useState } from 'react'

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

let sperren = 0
let altOverflow = ''
const stapel: HTMLElement[] = []

/**
 * Gemeinsames Verhalten modaler Overlays: Esc schließt (nur das oberste), Tab bleibt im Container (Fokusfalle),
 * Fokus geht beim Schließen zurück, Hintergrund-Scroll ist gesperrt.
 * `initialSelector` bestimmt das zuerst fokussierte Element (Standard: erstes fokussierbares).
 */
export function useModal(open: boolean, container: RefObject<HTMLElement>, onClose: () => void, initialSelector?: string) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) return
    const vorher = document.activeElement as HTMLElement | null
    if (sperren++ === 0) { altOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden' }

    const el = container.current
    if (el) stapel.push(el)
    const raf = requestAnimationFrame(() => {
      const ziel = (initialSelector && el?.querySelector<HTMLElement>(initialSelector)) || el?.querySelector<HTMLElement>(FOCUSABLE) || el
      ziel?.focus({ preventScroll: true })
    })

    const onKey = (e: KeyboardEvent) => {
      const root = container.current
      if (!root) return
      if (e.key === 'Escape') {
        // nur das oberste Overlay reagiert: Container muss das zuletzt geöffnete sein
        if (stapel.length && stapel[stapel.length - 1] !== root) return
        e.stopPropagation(); e.preventDefault(); closeRef.current()
        return
      }
      if (e.key !== 'Tab') return
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(n => n.offsetParent !== null || n === document.activeElement)
      if (items.length === 0) { e.preventDefault(); root.focus(); return }
      const first = items[0], last = items[items.length - 1]
      const aktiv = document.activeElement as HTMLElement | null
      if (!aktiv || !root.contains(aktiv)) { e.preventDefault(); first.focus(); return }
      if (e.shiftKey && aktiv === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && aktiv === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey, true)

    return () => {
      cancelAnimationFrame(raf)
      const i = el ? stapel.lastIndexOf(el) : -1
      if (i >= 0) stapel.splice(i, 1)
      document.removeEventListener('keydown', onKey, true)
      if (--sperren === 0) document.body.style.overflow = altOverflow
      if (vorher && document.contains(vorher)) vorher.focus({ preventScroll: true })
    }
  }, [open, container, initialSelector])
}

const Q_SHEET = '(max-width: 767.98px)'

/** true unter 768 px: Dialoge werden als Vollbild-Sheet von unten dargestellt. */
export function useSheetMode(): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(Q_SHEET).matches)
  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia(Q_SHEET)
    const f = () => setOn(mq.matches)
    f()
    mq.addEventListener('change', f)
    return () => mq.removeEventListener('change', f)
  }, [])
  return on
}

/** Sichtbarer Bereich (visualViewport): schrumpft bei offener Bildschirmtastatur. */
export function useVisualViewport(active: boolean): { height: number; top: number } | null {
  const [vv, setVv] = useState<{ height: number; top: number } | null>(null)
  useEffect(() => {
    const v = typeof window !== 'undefined' ? window.visualViewport : null
    if (!active || !v) { setVv(null); return }
    const f = () => setVv({ height: v.height, top: v.offsetTop })
    f()
    v.addEventListener('resize', f)
    v.addEventListener('scroll', f)
    return () => { v.removeEventListener('resize', f); v.removeEventListener('scroll', f) }
  }, [active])
  return vv
}
